import { useState, SyntheticEvent } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack
} from '@mui/material';

// third-party
import * as Yup from 'yup';
import { Formik } from 'formik';

// project-imports
import useAuth from 'hooks/useAuth';
import IconButton from 'components/@extended/IconButton';
import AnimateButton from 'components/@extended/AnimateButton';
import PasswordStrengthMeter from 'components/@extended/PasswordStrengthMeter';
import { dispatch } from 'store';
import { openSnackbar } from 'store/reducers/snackbar';
import { strengthColor, strengthIndicator } from 'utils/password-strength';
import serverCall from 'api/serverCall';
import api from 'api/api';

// types
import { StringColorProps } from 'types/password';

// assets
import { Eye, EyeSlash } from 'iconsax-react';

interface Props {
  open: boolean;
  handleClose: () => void;
}

const ChangePasswordModal = ({ open, handleClose }: Props) => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [level, setLevel] = useState<StringColorProps>();
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleClickShowNewPassword = () => setShowNewPassword(!showNewPassword);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

  const handleMouseDownPassword = (event: SyntheticEvent) => {
    event.preventDefault();
  };

  const changePasswordStrength = (value: string) => {
    const temp = strengthIndicator(value);
    setLevel(strengthColor(temp));
  };

  return (
    <Dialog open={open} onClose={handleClose} keepMounted fullWidth maxWidth="sm" aria-labelledby="change-password-title">
      <DialogTitle id="change-password-title">Change Password</DialogTitle>
      <DialogContent sx={{ mt: 1 }}>
        <Formik
          initialValues={{
            newPassword: '',
            confirmPassword: '',
            submit: null
          }}
          validationSchema={Yup.object().shape({
            newPassword: Yup.string().max(255).required('New Password is required'),
            confirmPassword: Yup.string()
              .required('Confirm Password is required')
              .test('confirmPassword', 'Both Passwords must match!', (confirmPassword, yup) => yup.parent.newPassword === confirmPassword)
          })}
          onSubmit={(values, { setErrors, setStatus, setSubmitting, resetForm }) => {
            const payload = {
              newPassword: values.newPassword,
              confirmPassword: values.confirmPassword
            };

            serverCall.post(api.changePassword(), payload, (response: any, error: any) => {
              if (error) {
                setStatus({ success: false });
                const errorMessage = error?.response?.data?.error || error?.message || 'Failed to change password. Please try again.';
                setErrors({ submit: errorMessage });
                setSubmitting(false);
              } else {
                setStatus({ success: true });
                setSubmitting(false);
                dispatch(
                  openSnackbar({
                    open: true,
                    message: response?.message || 'Password changed successfully. Please login again.',
                    variant: 'alert',
                    alert: { color: 'success' },
                    close: false
                  })
                );
                resetForm();
                setLevel(undefined);
                handleClose();

                setTimeout(() => {
                  logout()
                    .then(() => {
                      navigate('/login', { replace: true });
                    })
                    .catch(() => {
                      navigate('/login', { replace: true });
                    });
                }, 1500);
              }
            });
          }}
        >
          {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="new-password">New Password</InputLabel>
                    <OutlinedInput
                      fullWidth
                      error={Boolean(touched.newPassword && errors.newPassword)}
                      id="new-password"
                      type={showNewPassword ? 'text' : 'password'}
                      value={values.newPassword}
                      name="newPassword"
                      onBlur={handleBlur}
                      onChange={(e) => {
                        handleChange(e);
                        changePasswordStrength(e.target.value);
                      }}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowNewPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                            color="secondary"
                          >
                            {showNewPassword ? <Eye /> : <EyeSlash />}
                          </IconButton>
                        </InputAdornment>
                      }
                      placeholder="Enter new password"
                    />
                    {touched.newPassword && errors.newPassword && (
                      <FormHelperText error id="helper-text-new-password">
                        {errors.newPassword as string}
                      </FormHelperText>
                    )}
                  </Stack>
                  {values.newPassword && level && <PasswordStrengthMeter level={level} />}
                </Grid>

                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="confirm-password">Confirm New Password</InputLabel>
                    <OutlinedInput
                      fullWidth
                      error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                      id="confirm-password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={values.confirmPassword}
                      name="confirmPassword"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowConfirmPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                            color="secondary"
                          >
                            {showConfirmPassword ? <Eye /> : <EyeSlash />}
                          </IconButton>
                        </InputAdornment>
                      }
                      placeholder="Confirm new password"
                    />
                    {touched.confirmPassword && errors.confirmPassword && (
                      <FormHelperText error id="helper-text-confirm-password">
                        {errors.confirmPassword as string}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>

                {errors.submit && (
                  <Grid item xs={12}>
                    <FormHelperText error>{errors.submit as string}</FormHelperText>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button onClick={handleClose} color="secondary" variant="outlined" disabled={isSubmitting}>
                      Cancel
                    </Button>
                    <AnimateButton>
                      <Button disableElevation disabled={isSubmitting} type="submit" variant="contained" color="primary">
                        Change Password
                      </Button>
                    </AnimateButton>
                  </Stack>
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordModal;
